'use client'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { 
  Brain,
  Shield,
  Target,
  TrendingUp,
  Users,
  BarChart3,
  GitBranch,
  Database,
  CheckCircle2,
  Zap,
  Calendar,
  MessageSquare,
  FileText
} from 'lucide-react'

import { FadeUp } from '@/components/ui/fade-up'
const services = [
  {
    icon: Target,
    title: 'IT戦略策定',
    description: '組織のビジョンに合わせた中長期的なIT戦略を策定。DXロードマップの作成から実行支援まで。'
  },
  {
    icon: GitBranch,
    title: 'システム選定',
    description: '要件に最適なシステムを中立的な立場で評価・選定。ベンダー交渉のサポートも実施。'
  },
  {
    icon: Database,
    title: 'データ設計',
    description: 'データ活用戦略の立案から、データ基盤の設計、ガバナンス体制の構築まで包括的に支援。'
  },
  {
    icon: Shield,
    title: 'ガバナンス構築',
    description: 'ITガバナンス、セキュリティポリシー、リスク管理体制を組織に合わせて設計・導入。'
  },
  {
    icon: BarChart3,
    title: '運用レビュー',
    description: '定期的な運用状況の評価と改善提案。KPI管理とROI最大化を継続的にサポート。'
  },
  {
    icon: Users,
    title: '人材育成支援',
    description: 'IT部門の組織設計と、内製化に向けた人材育成プログラムの企画・実施。'
  }
]

const benefits = [
  {
    title: '専門性の即座な活用',
    description: 'CIOレベルの専門知識を必要な時に必要な分だけ活用',
    icon: Brain,
    results: [
      '意思決定スピード 3倍',
      '戦略ミスの削減 80%',
      '投資対効果 2.5倍'
    ]
  },
  {
    title: '客観的な視点',
    description: 'しがらみのない中立的な立場から最適解を提案',
    icon: Target,
    results: [
      'ベンダーロックイン回避',
      'コスト削減 平均30%',
      '透明性の向上'
    ]
  },
  {
    title: '継続的な成長',
    description: '組織のIT成熟度に合わせた段階的な内製化支援',
    icon: TrendingUp,
    results: [
      'IT人材の定着率向上',
      '内製化率 60%達成',
      'ナレッジの蓄積'
    ]
  }
]

const plans = [
  {
    name: 'ライト',
    price: '月額 30万円〜',
    description: '小規模組織・スタートアップ向け',
    features: [
      '月1回の定例会議（2時間）',
      'メール相談（月10件まで）',
      '四半期レビュー',
      '基本的な戦略アドバイス'
    ],
    commitment: '6ヶ月〜'
  },
  {
    name: 'スタンダード',
    price: '月額 80万円〜',
    description: '中規模組織・成長企業向け',
    features: [
      '月2回の定例会議（各2時間）',
      '随時相談（無制限）',
      '月次レビューレポート',
      'プロジェクト参画（月40時間）',
      'ベンダー選定支援',
      '人材育成プログラム'
    ],
    commitment: '1年〜',
    popular: true
  },
  {
    name: 'エンタープライズ',
    price: '月額 150万円〜',
    description: '大規模組織・自治体向け',
    features: [
      '週次定例会議',
      '常時アドバイザリー',
      'ボード報告支援',
      'フルタイム相当の関与',
      '複数拠点対応',
      'CTO/情報システム部門の代行'
    ],
    commitment: '1年〜'
  }
]

export function CioServiceContent() {
  return (
    <>
      {/* ヒーローセクション */}
      <section className="relative bg-gradient-to-br from-slate-800 via-slate-900 to-black text-white py-24 overflow-hidden">
        <div className="absolute inset-0 bg-[url('/images/grid.svg')] opacity-20"></div>
        <div
          className="animate-fade-up container relative z-10"
        >
          <div className="max-w-4xl mx-auto">
            <Badge className="mb-4 bg-white/20 text-white border-white/30">IT戦略のパートナー</Badge>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              CIO as a Service
            </h1>
            <p className="text-xl md:text-2xl mb-8 opacity-90">
              意思決定の質と内製化力の継続向上
            </p>
            <p className="text-lg mb-8 opacity-80">
              IT戦略・システム選定・データ設計・ガバナンス、運用レビュー。<br />
              月次定例＋随時相談＋スポット実装で、<br />
              組織のデジタル変革を戦略レベルから支援します。
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button size="lg" variant="secondary">
                <Calendar className="mr-2 h-5 w-5" />
                無料相談を予約
              </Button>
              <Button size="lg" variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20">
                サービス資料
              </Button>
            </div>
          </div>
        </div>
        <div className="absolute -bottom-1 left-0 right-0">
          <svg viewBox="0 0 1440 100" className="w-full h-24 fill-background">
            <path d="M0,50 C360,100 1080,0 1440,50 L1440,100 L0,100 Z" />
          </svg>
        </div>
      </section>

      {/* 価値提案 */}
      <section className="py-16">
        <div className="container">
          <FadeUp
            className="max-w-3xl mx-auto text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4">
              なぜCIO as a Serviceなのか
            </h2>
            <p className="text-lg text-muted-foreground">
              優秀なCIOの採用は困難。でも、IT戦略の遅れは組織の成長を止めてしまう。<br />
              必要な時に、必要な分だけ、CIOレベルの支援を。
            </p>
          </FadeUp>

          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon
              return (
                <FadeUp delay={index * 0.1}
                  key={index}
                >
                  <Card className="h-full">
                    <CardHeader>
                      <Icon className="h-12 w-12 text-primary mb-4" />
                      <CardTitle>{benefit.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground mb-4">{benefit.description}</p>
                      <ul className="space-y-2">
                        {benefit.results.map((result, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-sm">
                            <CheckCircle2 className="h-4 w-4 text-primary" />
                            <span className="font-medium">{result}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </FadeUp>
              )
            })}
          </div>
        </div>
      </section>

      {/* サービス内容 */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <FadeUp
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4">サービス内容</h2>
            <p className="text-lg text-muted-foreground">
              IT経営の全領域をカバーする包括的な支援
            </p>
          </FadeUp>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => {
              const Icon = service.icon
              return (
                <FadeUp delay={index * 0.05}
                  key={index}
                >
                  <Card className="h-full">
                    <CardHeader>
                      <Icon className="h-8 w-8 text-primary mb-2" />
                      <CardTitle className="text-lg">{service.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground">{service.description}</p>
                    </CardContent>
                  </Card>
                </FadeUp>
              )
            })}
          </div>
        </div>
      </section>

      {/* 支援の流れ */}
      <section className="py-16">
        <div className="container">
          <FadeUp
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4">支援の流れ</h2>
            <p className="text-lg text-muted-foreground">
              継続的な改善サイクルで組織のIT成熟度を向上
            </p>
          </FadeUp>

          <div className="max-w-4xl mx-auto">
            <Tabs defaultValue="monthly" className="space-y-8">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="monthly">月次サイクル</TabsTrigger>
                <TabsTrigger value="quarterly">四半期サイクル</TabsTrigger>
                <TabsTrigger value="yearly">年次サイクル</TabsTrigger>
              </TabsList>

              <TabsContent value="monthly" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>月次定例会議</CardTitle>
                    <CardDescription>毎月の定期的な戦略レビューと意思決定支援</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {[
                      { icon: FileText, task: '前月の振り返りとKPI評価' },
                      { icon: MessageSquare, task: '現在進行中のプロジェクトレビュー' },
                      { icon: Target, task: '新規課題の洗い出しと優先順位付け' },
                      { icon: Calendar, task: '翌月のアクションプラン策定' }
                    ].map((item, idx) => {
                      const Icon = item.icon
                      return (
                        <div key={idx} className="flex items-center gap-3">
                          <Icon className="h-5 w-5 text-primary" />
                          <span>{item.task}</span>
                        </div>
                      )
                    })}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="quarterly" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>四半期レビュー</CardTitle>
                    <CardDescription>3ヶ月ごとの包括的な評価と戦略調整</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {[
                      { icon: BarChart3, task: 'IT投資のROI分析' },
                      { icon: GitBranch, task: 'アーキテクチャの見直し' },
                      { icon: Users, task: '組織・人材の評価と育成計画' },
                      { icon: Shield, task: 'セキュリティ監査と改善' }
                    ].map((item, idx) => {
                      const Icon = item.icon
                      return (
                        <div key={idx} className="flex items-center gap-3">
                          <Icon className="h-5 w-5 text-primary" />
                          <span>{item.task}</span>
                        </div>
                      )
                    })}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="yearly" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>年次戦略会議</CardTitle>
                    <CardDescription>年間の総括と次年度戦略の立案</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {[
                      { icon: TrendingUp, task: 'IT成熟度の総合評価' },
                      { icon: Target, task: '中長期ロードマップの更新' },
                      { icon: Database, task: '技術スタックの最適化' },
                      { icon: Brain, task: '次世代技術の導入検討' }
                    ].map((item, idx) => {
                      const Icon = item.icon
                      return (
                        <div key={idx} className="flex items-center gap-3">
                          <Icon className="h-5 w-5 text-primary" />
                          <span>{item.task}</span>
                        </div>
                      )
                    })}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </section>

      {/* 料金プラン */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <FadeUp
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4">料金プラン</h2>
            <p className="text-lg text-muted-foreground">
              組織の規模とニーズに合わせた柔軟なプラン
            </p>
          </FadeUp>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {plans.map((plan, index) => (
              <FadeUp delay={index * 0.1}
                key={index}
              >
                <Card className={`h-full ${plan.popular ? 'border-primary shadow-lg' : ''}`}>
                  {plan.popular && (
                    <div className="bg-primary text-white text-center py-2 text-sm font-semibold">
                      人気プラン
                    </div>
                  )}
                  <CardHeader>
                    <CardTitle>{plan.name}</CardTitle>
                    <CardDescription>{plan.description}</CardDescription>
                    <div className="text-3xl font-bold mt-4">{plan.price}</div>
                    <Badge variant="secondary" className="mt-2">
                      最低契約期間: {plan.commitment}
                    </Badge>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {plan.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <CheckCircle2 className="h-5 w-5 text-primary shrink-0" />
                          <span className="text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Button className="w-full mt-6" variant={plan.popular ? 'default' : 'outline'}>
                      相談する
                    </Button>
                  </CardContent>
                </Card>
              </FadeUp>
            ))}
          </div>

          <div className="mt-8 text-center">
            <p className="text-sm text-muted-foreground">
              ※ スポット実装（システム開発等）は別途お見積もり
            </p>
          </div>
        </div>
      </section>

      {/* 導入事例 */}
      <section className="py-16">
        <div className="container">
          <FadeUp
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4">導入事例</h2>
            <p className="text-lg text-muted-foreground">
              様々な組織でIT戦略の高度化を実現
            </p>
          </FadeUp>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {[
              {
                company: '広島県某市役所',
                challenge: '老朽化したシステムの刷新と、職員のITリテラシー向上',
                solution: 'CIOaaSによる戦略立案と段階的な移行計画の策定',
                results: [
                  'システム運用コスト40%削減',
                  '業務効率30%向上',
                  'IT人材3名を内部育成'
                ],
                period: '2年間の支援'
              },
              {
                company: '地域金融機関',
                challenge: 'DX推進の遅れと、レガシーシステムからの脱却',
                solution: 'クラウド移行戦略の立案と、アジャイル開発体制の構築',
                results: [
                  '新サービス開発期間を1/3に短縮',
                  'API連携で外部サービスと接続',
                  '若手IT人材の定着率向上'
                ],
                period: '18ヶ月の支援'
              }
            ].map((caseStudy, index) => (
              <FadeUp delay={index * 0.1}
                key={index}
              >
                <Card>
                  <CardHeader>
                    <CardTitle>{caseStudy.company}</CardTitle>
                    <Badge variant="outline">{caseStudy.period}</Badge>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <p className="text-sm font-semibold text-muted-foreground mb-1">課題</p>
                      <p className="text-sm">{caseStudy.challenge}</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-muted-foreground mb-1">ソリューション</p>
                      <p className="text-sm">{caseStudy.solution}</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-muted-foreground mb-2">成果</p>
                      <ul className="space-y-1">
                        {caseStudy.results.map((result, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-sm">
                            <Zap className="h-4 w-4 text-primary" />
                            <span>{result}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-slate-800 to-slate-900 text-white">
        <div className="container">
          <FadeUp
            className="max-w-3xl mx-auto text-center"
          >
            <Brain className="h-16 w-16 mx-auto mb-6 opacity-80" />
            <h2 className="text-3xl font-bold mb-4">
              ITで、組織の未来を切り拓く
            </h2>
            <p className="text-xl mb-8 opacity-90">
              まずは無料相談で、貴組織の課題と可能性を探りましょう
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" variant="secondary">
                <Calendar className="mr-2 h-5 w-5" />
                無料相談を予約
              </Button>
              <Button size="lg" variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20">
                詳細資料をダウンロード
              </Button>
            </div>
          </FadeUp>
        </div>
      </section>
    </>
  )
}